import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { useUnsaved } from "@/components/UnsavedGuard";
import { Database, Boxes, BookOpen, UtensilsCrossed, Calculator, LogOut, ChefHat, Package, Menu, X } from "lucide-react";

const NAV = [
  { to: "/ingredients", label: "L0 Raw Materials", icon: Database, testid: "nav-ingredients" },
  { to: "/database", label: "Ingredient Database", icon: Boxes, testid: "nav-database" },
  { to: "/recipes", label: "Recipe Directory", icon: BookOpen, testid: "nav-recipes" },
  { to: "/menu", label: "L3 Menu Dashboard", icon: UtensilsCrossed, testid: "nav-menu" },
  { to: "/planners", label: "Production Planners", icon: Calculator, testid: "nav-planners" },
  { to: "/b2b-pricing", label: "B2B Price Calculator", icon: Package, testid: "nav-b2b-pricing" },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const { confirmLeave } = useUnsaved();
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const resize = () => { if (desktop.matches) setNavOpen(false); };
    const escape = e => { if (e.key === 'Escape') setNavOpen(false); };
    desktop.addEventListener('change', resize);
    window.addEventListener('keydown', escape);
    return () => { desktop.removeEventListener('change', resize); window.removeEventListener('keydown', escape); };
  }, []);
  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <div className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3 md:hidden">
        <button data-testid="mobile-menu-open" aria-label="Open navigation" aria-expanded={navOpen} onClick={() => setNavOpen(true)}><Menu size={20} /></button>
        <span className="text-sm font-semibold">RecipeCost</span>
      </div>
      {navOpen && <button data-testid="mobile-menu-overlay" aria-label="Close navigation" className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setNavOpen(false)} />}
      <aside className={`fixed left-0 top-0 z-40 h-full w-64 max-w-full flex-col border-r border-zinc-200 bg-zinc-50 md:flex ${navOpen ? 'flex animate-in fade-in duration-150' : 'hidden'}`}>
        <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-4">
          <ChefHat className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight">RecipeCost</div>
            <div className="truncate text-[11px] text-zinc-500" data-testid="workspace-name">{user?.workspace_name} · ₹ INR</div>
          </div>
          <button data-testid="mobile-menu-close" aria-label="Close navigation" className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-sm transition-colors hover:bg-zinc-200 md:hidden" onClick={() => setNavOpen(false)}><X size={18} /></button>
        </div>
        <nav className="flex-1 space-y-0.5 p-2">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={(e) => { if (!confirmLeave()) e.preventDefault(); else setNavOpen(false); }}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-sm px-3 py-2 text-[13px] transition-colors duration-150 ${
                  isActive ? "bg-zinc-200/60 font-medium text-zinc-950" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-3">
          <div className="mb-2 truncate text-xs text-zinc-500">{user?.email}</div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-100"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 min-h-screen md:ml-64">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
